/** Pure layout/formatting helpers behind the compact Notifications tab.
 *
 *  Kept free of React and of `api/client` so the rules the design depends on
 *  (which column a trigger lands in, what a stepper shows, what "money-in only"
 *  clears) are testable on their own.
 */

/** The subset of the API's InterruptStatus these helpers read. */
export interface InterruptLike {
  id: number;
  name: string;
  status: boolean;
}

/** The design's two trigger columns, plus an "other" fallback so a new backend
 *  trigger is never silently hidden. */
export type InterruptColumn = 'sales' | 'risk' | 'other';

/** Which column a trigger belongs to. Risk is tested first: a few names match
 *  both patterns (e.g. fan_asks_off_platform…) and risk must win. */
export function classifyInterrupt(name: string): InterruptColumn {
  const norm = name.toLowerCase();
  if (/(off.?platform|external|banned|not.?happy|no.?money|media|complain|suspect|other_model_page|page|stopped.?responding)/.test(norm)) {
    return 'risk';
  }
  if (/asks|content|price|pricing|custom|call|body|look/.test(norm)) {
    return 'sales';
  }
  return 'other';
}

export interface InterruptGroups<T extends InterruptLike> {
  sales: T[];
  risk: T[];
  other: T[];
}

/** Triggers the tab renders with a dedicated control instead of a plain toggle,
 *  so they must not also appear in the two columns. fan_sent_media_file is the
 *  three-way "Fan sent media" selector. */
const OWN_CONTROL = new Set(['fan_sent_media_file']);

/** Triggers split out of an older, broader one. The API lists triggers by creation order,
 *  which would park these at the bottom; they are shown right after the one they came from. */
const SPLIT_FROM: Record<string, string> = {
  fan_asks_about_catalog: 'fan_asks_for_content',
  fan_wants_to_buy_content: 'fan_asks_about_catalog',
  fan_suspects_ai_or_fake: 'fan_not_happy',
  fan_offers_external_payment: 'fan_asks_off_platform_or_external_payment',
  fan_asks_for_verification_photo: 'fan_asks_how_model_looks_now',
};

function placeSplitTriggers<T extends InterruptLike>(column: T[]): T[] {
  const out = column.filter((n) => !(n.name in SPLIT_FROM));
  for (const [child, parent] of Object.entries(SPLIT_FROM)) {
    const n = column.find((m) => m.name === child);
    if (n === undefined) continue;
    const at = out.findIndex((m) => m.name === parent);
    if (at === -1) out.push(n);
    else out.splice(at + 1, 0, n);
  }
  return out;
}

/** Split the dynamic trigger list into the design's columns, preserving order. */
export function groupInterrupts<T extends InterruptLike>(items: T[]): InterruptGroups<T> {
  const out: InterruptGroups<T> = { sales: [], risk: [], other: [] };
  for (const n of items) {
    if (OWN_CONTROL.has(n.name.trim().toLowerCase())) continue;
    out[classifyInterrupt(n.name)].push(n);
  }
  return { sales: placeSplitTriggers(out.sales), risk: placeSplitTriggers(out.risk), other: placeSplitTriggers(out.other) };
}

/** "enabled N of M" for the interrupts card header, over every column shown. */
export function interruptsEnabledSummary<T extends InterruptLike>(
  groups: InterruptGroups<T>,
): { enabled: number; total: number } {
  const all = [...groups.sales, ...groups.risk, ...groups.other];
  return { enabled: all.filter((n) => n.status).length, total: all.length };
}

/** Detection time for "fan stopped responding". The design shows it in minutes
 *  and steps by 5; the API stores seconds. */
export const DETECTION_STEP_SECONDS = 300;
export const DETECTION_MIN_SECONDS = 300;
export const DETECTION_MAX_SECONDS = 14400;

/** Step the stored seconds by one 5-minute notch, snapping an off-grid legacy
 *  value (e.g. a 90s value set before this control existed) onto the grid first
 *  so a single click always lands somewhere round. */
export function stepDetectionSeconds(seconds: number, direction: 1 | -1): number {
  const grid = direction > 0
    ? Math.floor(seconds / DETECTION_STEP_SECONDS) * DETECTION_STEP_SECONDS
    : Math.ceil(seconds / DETECTION_STEP_SECONDS) * DETECTION_STEP_SECONDS;
  const next = grid + direction * DETECTION_STEP_SECONDS;
  return Math.min(DETECTION_MAX_SECONDS, Math.max(DETECTION_MIN_SECONDS, next));
}

/** Minute label for the stepper: "30", or "1.5" for an off-grid legacy value. */
export function detectionMinutesLabel(seconds: number): string {
  const mins = Math.round((seconds / 60) * 10) / 10;
  return Number.isInteger(mins) ? String(mins) : mins.toFixed(1);
}

/** Long-chat flag (ADR 0054). 0 / blank = off; the design steps by 5 AI turns. */
export const TURN_STEP = 5;
export const TURN_MAX = 200;

export function stepTurnThreshold(value: number | '', direction: 1 | -1): number {
  const current = value === '' ? 0 : value;
  const next = current + direction * TURN_STEP;
  return Math.min(TURN_MAX, Math.max(0, next));
}

/** The list/notify sub-row only appears once the flag is actually armed. */
export function isLongChatFlagOn(value: number | ''): boolean {
  return value !== '' && value > 0;
}

/** Chat-tracking cleanup window, in days. */
export const CLEANUP_MIN_DAYS = 1;
export const CLEANUP_MAX_DAYS = 30;

export function stepCleanupDays(days: number, direction: 1 | -1): number {
  return Math.min(CLEANUP_MAX_DAYS, Math.max(CLEANUP_MIN_DAYS, days + direction));
}

/** Activity opt-out flags. true = deliver the ping. Money-in pings (PPV purchased,
 *  tip received) are always delivered and carry no flag. allPaymentEvents is the one
 *  opt-IN (ADR 0053): notify on payment events the AI did not make.
 *
 *  Every flag here is notification-only: silencing one changes nothing the fan sees.
 *  `fanRequests` used to sit in this list and did NOT honour that promise — it muted
 *  the ping for an interrupt that had already handed the chat over, so the fan was
 *  parked in the interrupt folder and the AI went quiet with nobody told. It is gone;
 *  those alerts are governed by the interrupt's own on/off switch. */
export interface ActivityOptOuts {
  ppvSent: boolean;
  freeContentSent: boolean;
  tipAsk: boolean;
  vipOfferSent: boolean;
  chatEnded: boolean;
  allPaymentEvents: boolean;
}

/** The flags the "money-in only" shortcut clears — allPaymentEvents is an opt-in,
 *  not an activity ping, so the shortcut leaves it as the operator set it. The
 *  shortcut only ever touches notification-only flags, so it can never silence the
 *  AI or hand a chat over. */
const SILENCEABLE = ['ppvSent', 'freeContentSent', 'tipAsk', 'vipOfferSent', 'chatEnded'] as const;

export function moneyInOnly(current: ActivityOptOuts): ActivityOptOuts {
  const next = { ...current };
  for (const k of SILENCEABLE) next[k] = false;
  return next;
}

export function canApplyMoneyInOnly(current: ActivityOptOuts): boolean {
  return SILENCEABLE.some((k) => current[k]);
}

/** Whether a freshly-typed Telegram chat ID needs persisting, and what to send.
 *  The field autosaves as the operator types (debounced by the caller); every
 *  other control on the tab still commits through the Save pill. */
export function telegramAutosaveTarget(
  typed: string,
  saved: string | null | undefined,
): { shouldSave: boolean; value: string | null } {
  const next = typed.trim();
  const current = (saved ?? '').trim();
  if (next === current) return { shouldSave: false, value: next || null };
  return { shouldSave: true, value: next || null };
}
